"use client";

import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Appfooter from "../components/Appfooter";
import Popupchat from "../components/Popupchat";

import { updateUserProfileDetailsApi } from "../api/UserActions";

import {
  saveProfessionDetails,
  getProfessionDetails,
  resetProfessionError,
  resetProfessionMessage,
} from "../redux/slices/professionSlice";

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

const getUploadUrl = (file?: string | null) => {
  if (!file || String(file).trim() === "") {
    return "/assets/images/user.png";
  }

  const value = String(file).trim();

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return encodeURI(value);
  }

  if (value.startsWith("/uploads/")) {
    return encodeURI(`${API_URL}${value}`);
  }

  if (value.startsWith("uploads/")) {
    return encodeURI(`${API_URL}/${value}`);
  }

  if (value.startsWith("/")) {
    return encodeURI(`${API_URL}${value}`);
  }

  return encodeURI(`${API_URL}/uploads/${value}`);
};

type WorkType = "job" | "business" | "";

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const [currentUser, setCurrentUser] = useState<any>(() =>
    JSON.parse(localStorage.getItem("user") || "{}"),
  );

  const user = currentUser;

  const isGuest =
    !user?.user_id ||
    String(user?.role || "").toLowerCase() === "guest";

  const {
    professionDetails,
    saveLoading,
    error: professionError,
    message: professionMessage,
  } = useSelector((state: any) => state.profession);

  const [workType, setWorkType] = useState<WorkType>("");
  const [pendingWorkType, setPendingWorkType] = useState<WorkType | null>(null);
  const [showSwitchDialog, setShowSwitchDialog] = useState(false);

  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);

  const [profileForm, setProfileForm] = useState({
    user_fullname: user?.user_fullname || "",
    user_email: user?.user_email || user?.email || "",
    dob: user?.dob ? String(user.dob).slice(0, 10) : "",
  });

  const [formData, setFormData] = useState({
    user_id: user?.user_id || "",
    work_type: "",

    job_title: "",
    company_name: "",
    job_department: "",
    job_experience: "",
    job_location: "",
    job_description: "",

    business_name: "",
    business_category: "",
    business_mobile: "",
    business_email: "",
    business_address: "",
    business_website: "",
    business_description: "",
  });

  useEffect(() => {
    const refreshUser = () => {
      setCurrentUser(JSON.parse(localStorage.getItem("user") || "{}"));
    };

    window.addEventListener("userProfileUpdated", refreshUser);
    window.addEventListener("storage", refreshUser);

    return () => {
      window.removeEventListener("userProfileUpdated", refreshUser);
      window.removeEventListener("storage", refreshUser);
    };
  }, []);

  useEffect(() => {
    setProfileForm({
      user_fullname: user?.user_fullname || "",
      user_email: user?.user_email || user?.email || "",
      dob: user?.dob ? String(user.dob).slice(0, 10) : "",
    });
  }, [user?.user_id, user?.user_fullname, user?.user_email, user?.dob]);

  useEffect(() => {
    if (isGuest || !user?.user_id) return;

    setFormData((prev) => ({
      ...prev,
      user_id: user.user_id,
    }));

    dispatch(getProfessionDetails(user.user_id));
  }, [dispatch, isGuest, user?.user_id]);

  useEffect(() => {
    if (!professionDetails) return;

    setWorkType(professionDetails.work_type || "");

    setFormData((prev) => ({
      ...prev,
      user_id: professionDetails.user_id || user?.user_id || "",
      work_type: professionDetails.work_type || "",

      job_title: professionDetails.job_title || "",
      company_name: professionDetails.company_name || "",
      job_department: professionDetails.job_department || "",
      job_experience: professionDetails.job_experience || "",
      job_location: professionDetails.job_location || "",
      job_description: professionDetails.job_description || "",

      business_name: professionDetails.business_name || "",
      business_category: professionDetails.business_category || "",
      business_mobile: professionDetails.business_mobile || "",
      business_email: professionDetails.business_email || "",
      business_address: professionDetails.business_address || "",
      business_website: professionDetails.business_website || "",
      business_description: professionDetails.business_description || "",
    }));
  }, [professionDetails, user?.user_id]);

  useEffect(() => {
    if (!professionMessage) return;

    const hiddenMessages = [
      "Profession details fetched successfully",
      "Profession fetched successfully",
    ];

    const shouldHideToast = hiddenMessages.some((msg) =>
      String(professionMessage).toLowerCase().includes(msg.toLowerCase()),
    );

    if (!shouldHideToast) {
      toast.success(professionMessage);
    }

    dispatch(resetProfessionMessage());
  }, [professionMessage, dispatch]);

  useEffect(() => {
    if (!professionError) return;

    toast.error(professionError);
    dispatch(resetProfessionError());
  }, [professionError, dispatch]);

  const profilePhoto = getUploadUrl(user?.profile_photo);

  const displayName =
    user?.user_fullname || user?.full_name || user?.name || "User";

  const email = user?.email || user?.user_email || "No email available";

  const phone =
    user?.phone_number || user?.user_mobile || user?.mobile || "Not updated";

  const gender = user?.gender || "Not updated";
  const city = user?.city || user?.location || "Not updated";

  const dob = user?.dob
    ? new Date(user.dob).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : user?.date_of_birth
      ? new Date(user.date_of_birth).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "Not updated";

  const handleLogout = () => {
    localStorage.clear();
    navigate("/home");
    window.location.reload();
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isGuest) {
      toast.info("Please log in to edit your profile.");
      return;
    }

    if (!user?.user_id) {
      toast.error("User not found");
      return;
    }

    try {
      setProfileSaving(true);

      const updateFormData = new FormData();
      updateFormData.append("user_id", String(user.user_id));
      updateFormData.append("user_fullname", profileForm.user_fullname || "");
      updateFormData.append("user_email", profileForm.user_email || "");
      updateFormData.append("dob", profileForm.dob || "");

      if (selectedPhoto) {
        updateFormData.append("profile_photo", selectedPhoto);
      }

      const result =
        await updateUserProfileDetailsApi(
          updateFormData
        );

      if (!result?.success) {
        toast.error(result?.message || "Failed to update profile");
        return;
      }

      const oldUser = JSON.parse(localStorage.getItem("user") || "{}");

      const updatedUser = {
        ...oldUser,
        ...result.data,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));

      setCurrentUser(updatedUser);
      setSelectedPhoto(null);
      setShowEditProfile(false);

      window.dispatchEvent(new Event("userProfileUpdated"));

      toast.success("Profile updated successfully");
    } catch (error) {
      console.log("Profile update error:", error);
      toast.error("Failed to update profile");
    } finally {
      setProfileSaving(false);
    }
  };

  const applyWorkTypeChange = (type: WorkType) => {
    setWorkType(type);

    setFormData((prev) => ({
      ...prev,
      work_type: type,

      job_title: type === "job" ? prev.job_title : "",
      company_name: type === "job" ? prev.company_name : "",
      job_department: type === "job" ? prev.job_department : "",
      job_experience: type === "job" ? prev.job_experience : "",
      job_location: type === "job" ? prev.job_location : "",
      job_description: type === "job" ? prev.job_description : "",

      business_name: type === "business" ? prev.business_name : "",
      business_category: type === "business" ? prev.business_category : "",
      business_mobile: type === "business" ? prev.business_mobile : "",
      business_email: type === "business" ? prev.business_email : "",
      business_address: type === "business" ? prev.business_address : "",
      business_website: type === "business" ? prev.business_website : "",
      business_description:
        type === "business" ? prev.business_description : "",
    }));
  };

  const handleWorkTypeChange = (type: WorkType) => {
    if (workType !== type && workType !== "") {
      setPendingWorkType(type);
      setShowSwitchDialog(true);
      return;
    }

    applyWorkTypeChange(type);
  };

  const handleConfirmSwitch = () => {
    if (pendingWorkType === null) return;

    applyWorkTypeChange(pendingWorkType);
    setPendingWorkType(null);
    setShowSwitchDialog(false);
    toast.info("Profession type changed. Previous details were cleared.");
  };

  const handleCancelSwitch = () => {
    setPendingWorkType(null);
    setShowSwitchDialog(false);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isGuest) {
      toast.info("Please log in to update profession details.");
      return;
    }

    const payload = {
      ...formData,
      user_id: user?.user_id || formData.user_id,
      // workType can be empty string for the None option.
      // The redux payload type only allows job/business, so cast payload here.
      work_type: workType,
    };

    dispatch(saveProfessionDetails(payload as any));
  };

  const currentProfession = workType
    ? workType === "job"
      ? "Job"
      : "Business"
    : "None";

  return (
    <Fragment>
      <Header />
      <Leftnav />

      {showSwitchDialog && (
        <div className="settings-dialog-backdrop">
          <div className="settings-dialog-box">
            <div className="settings-dialog-icon">
              <i className="feather-alert-triangle"></i>
            </div>

            <h4>Change Profession Type?</h4>
            <p>
              Changing profession type will clear the previously entered
              details. Do you want to continue?
            </p>

            <div className="settings-dialog-actions">
              <button
                type="button"
                className="dialog-cancel-btn"
                onClick={handleCancelSwitch}
              >
                Cancel
              </button>

              <button
                type="button"
                className="dialog-confirm-btn"
                onClick={handleConfirmSwitch}
              >
                Yes, Change
              </button>
            </div>
          </div>
        </div>
      )}

      {!isGuest && showEditProfile && (
        <div className="edit-profile-backdrop">
          <div className="edit-profile-modal">
            <div className="edit-profile-head">
              <div>
                <span>Edit Profile</span>
                <h4>Update your basic details</h4>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowEditProfile(false);
                  setSelectedPhoto(null);
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleProfileUpdate}>
              <div className="edit-profile-photo-row">
                <img
                  src={
                    selectedPhoto
                      ? URL.createObjectURL(selectedPhoto)
                      : profilePhoto
                  }
                  alt={displayName}
                  onError={(e: any) => {
                    e.currentTarget.src = "/assets/images/user.png";
                  }}
                />

                <div>
                  <label className="photo-upload-btn">
                    <i className="feather-camera"></i>
                    Change Photo
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setSelectedPhoto(file);
                      }}
                    />
                  </label>

                  <p>Upload JPG, PNG, or WEBP image.</p>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-12">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-control settings-input"
                    value={profileForm.user_fullname}
                    onChange={(e) =>
                      setProfileForm((prev) => ({
                        ...prev,
                        user_fullname: e.target.value,
                      }))
                    }
                    placeholder="Enter full name"
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-control settings-input"
                    value={profileForm.user_email}
                    onChange={(e) =>
                      setProfileForm((prev) => ({
                        ...prev,
                        user_email: e.target.value,
                      }))
                    }
                    placeholder="Enter email"
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label">Date of Birth</label>
                  <input
                    type="date"
                    className="form-control settings-input"
                    value={profileForm.dob}
                    onChange={(e) =>
                      setProfileForm((prev) => ({
                        ...prev,
                        dob: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="edit-profile-actions">
                <button
                  type="button"
                  className="edit-cancel-btn"
                  onClick={() => {
                    setShowEditProfile(false);
                    setSelectedPhoto(null);
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="edit-save-btn"
                  disabled={profileSaving}
                >
                  <i className="feather-save"></i>
                  {profileSaving ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="main-content settings-page-main">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="settings-shell">
              <div
                className={`settings-top-grid ${
                  isGuest ? "guest-profile-only" : ""
                }`}
              >
                <div className="settings-profile-card profile-hero-card">
                  <div className="profile-hero-glow"></div>

                  <div className="profile-hero-main">
                    <div className="settings-profile-avatar-wrap">
                      <img
                        src={profilePhoto}
                        alt={displayName}
                        className="settings-profile-avatar"
                        onError={(e: any) => {
                          e.currentTarget.src = "/assets/images/user.png";
                        }}
                      />
                      <span className="settings-online-dot"></span>
                    </div>

                    <div className="settings-profile-info">
                      <span className="settings-chip">
                        <i className="feather-settings"></i>
                        Profile Settings
                      </span>

                      <h2>{displayName}</h2>
                      <p>{email}</p>

                      <div className="settings-mini-tags">
                        {!isGuest && (
                          <span className="success-tag">
                            <i className="feather-check-circle"></i>
                            Active Member
                          </span>
                        )}

                        <span>
                          <i className="feather-map-pin"></i>
                          {city}
                        </span>

                        {!isGuest && (
                          <span>
                            <i className="feather-briefcase"></i>
                            {currentProfession}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {!isGuest && (
                    <div className="profile-hero-footer">
                      <div className="profile-small-note">
                        <i className="feather-shield"></i>
                        <span>
                          Your profile information is visible based on your privacy settings.
                        </span>
                      </div>

                      <button
                        type="button"
                        className="edit-profile-main-btn"
                        onClick={() => setShowEditProfile(true)}
                      >
                        <i className="feather-edit-2"></i>
                        Edit Profile
                      </button>
                    </div>
                  )}
                </div>

                {!isGuest && (
                  <div className="settings-action-card">
                    <div className="action-card-top">
                      <div className="action-icon">
                        <i className="feather-log-out"></i>
                      </div>

                      <div className="action-text">
                        <span className="section-tag action-tag">
                          Quick Action
                        </span>
                        <h4>Account Access</h4>
                        <p>Logout securely from this browser session.</p>
                      </div>
                    </div>

                    <div className="session-box">
                      <div>
                        <span>Current Session</span>
                        <strong>Active now</strong>
                      </div>
                      <i className="feather-check-circle"></i>
                    </div>

                    <button
                      onClick={handleLogout}
                      className="settings-logout-btn"
                    >
                      <i className="feather-log-out"></i>
                      Logout
                    </button>
                  </div>
                )}
              </div>

              <div className="settings-grid">
                <div className="settings-card">
                  <div className="settings-card-header">
                    <div>
                      <span className="section-tag">Personal Info</span>
                      <h4>Personal Information</h4>
                      <p>Your basic account details are shown below.</p>
                    </div>
                  </div>

                  <div className="settings-info-grid">
                    {[
                      {
                        icon: "feather-user",
                        label: "Full Name",
                        value: displayName,
                      },
                      {
                        icon: "feather-mail",
                        label: "Email Address",
                        value: email,
                      },
                      {
                        icon: "feather-phone",
                        label: "Phone Number",
                        value: phone === "Not updated" ? phone : `+91 ${phone}`,
                      },
                      {
                        icon: "feather-calendar",
                        label: "Date of Birth",
                        value: dob,
                      },
                      {
                        icon: "feather-heart",
                        label: "Gender",
                        value: gender,
                      },
                      {
                        icon: "feather-map-pin",
                        label: "Location",
                        value: city,
                      },
                    ].map((item, index) => (
                      <div className="info-tile" key={index}>
                        <div className="info-icon">
                          <i className={item.icon}></i>
                        </div>

                        <div className="info-text">
                          <span>{item.label}</span>
                          <h5 title={item.value}>{item.value}</h5>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="settings-card summary-card">
                  <div className="settings-card-header">
                    <div>
                      <span className="section-tag">Account</span>
                      <h4>Account Summary</h4>
                      <p>Quick overview of your profile.</p>
                    </div>
                  </div>

                  <div className="summary-stack">
                    {!isGuest && (
                      <div className="summary-box">
                        <div className="summary-icon">
                          <i className="feather-shield"></i>
                        </div>

                        <div>
                          <span>Status</span>
                          <h6>Active Member</h6>
                        </div>
                      </div>
                    )}

                    <div className="summary-box">
                      <div className="summary-icon">
                        <i className="feather-map-pin"></i>
                      </div>

                      <div>
                        <span>Primary Base</span>
                        <h6>{city}</h6>
                      </div>
                    </div>

                    <div className="summary-box">
                      <div className="summary-icon">
                        <i className="feather-briefcase"></i>
                      </div>

                      <div>
                        <span>Profession Type</span>
                        <h6>{currentProfession}</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {!isGuest && (
                <div className="settings-card profession-card">
                  <form onSubmit={handleSubmit}>
                  <div className="settings-card-header profession-header">
                    <div>
                      <span className="section-tag">Professional Details</span>
                      <h4>Profession Type</h4>
                      <p>
                        Choose one profession type. User can save either Job or
                        Business details, not both.
                      </p>
                    </div>
                  </div>

                  <div className="selector-head">
                    <div>
                      <h5>What do you do?</h5>
                      <p>Pick one option below and fill only that form.</p>
                    </div>

                    <div className="single-choice-badge">
                      Single Choice Only
                    </div>
                  </div>

                  <div className="profession-choice-grid">
                    <button
                      type="button"
                      className={`work-type-card ${
                        workType === "job" ? "active" : ""
                      }`}
                      onClick={() => handleWorkTypeChange("job")}
                    >
                      <div className="work-left">
                        <span className="work-icon">
                          <i className="feather-briefcase"></i>
                        </span>

                        <span className="work-copy">
                          <strong>Job</strong>
                          <small>Working in a company or organization</small>
                        </span>
                      </div>

                      <span className="check-dot">
                        <i className="feather-check"></i>
                      </span>
                    </button>

                    <button
                      type="button"
                      className={`work-type-card ${
                        workType === "business" ? "active" : ""
                      }`}
                      onClick={() => handleWorkTypeChange("business")}
                    >
                      <div className="work-left">
                        <span className="work-icon">
                          <i className="feather-home"></i>
                        </span>

                        <span className="work-copy">
                          <strong>Business</strong>
                          <small>Own business, service, shop or firm</small>
                        </span>
                      </div>

                      <span className="check-dot">
                        <i className="feather-check"></i>
                      </span>
                    </button>

                    <button
                      type="button"
                      className={`work-type-card none-card ${
                        workType === "" ? "active" : ""
                      }`}
                      onClick={() => handleWorkTypeChange("")}
                    >
                      <div className="work-left">
                        <span className="work-icon">
                          <i className="feather-slash"></i>
                        </span>

                        <span className="work-copy">
                          <strong>None</strong>
                          <small>No professional details to display</small>
                        </span>
                      </div>

                      <span className="check-dot">
                        <i className="feather-check"></i>
                      </span>
                    </button>
                  </div>

                  {workType === "job" && (
                    <div className="profession-box">
                      <div className="profession-box-top">
                        <div>
                          <h5>Job Details</h5>
                          <p>Add your role, company and work details.</p>
                        </div>

                        <span className="box-icon">
                          <i className="feather-briefcase"></i>
                        </span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Job Title</label>
                          <input
                            type="text"
                            name="job_title"
                            className="form-control settings-input"
                            placeholder="Software Developer"
                            value={formData.job_title}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Company Name</label>
                          <input
                            type="text"
                            name="company_name"
                            className="form-control settings-input"
                            placeholder="Company name"
                            value={formData.company_name}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Department</label>
                          <input
                            type="text"
                            name="job_department"
                            className="form-control settings-input"
                            placeholder="IT / Sales / HR"
                            value={formData.job_department}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Experience</label>
                          <input
                            type="text"
                            name="job_experience"
                            className="form-control settings-input"
                            placeholder="2 years"
                            value={formData.job_experience}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="form-label">Job Location</label>
                          <input
                            type="text"
                            name="job_location"
                            className="form-control settings-input"
                            placeholder="Udupi, Karnataka"
                            value={formData.job_location}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="form-label">Job Description</label>
                          <textarea
                            name="job_description"
                            className="form-control settings-input"
                            rows={4}
                            placeholder="Briefly describe your work"
                            value={formData.job_description}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {workType === "business" && (
                    <div className="profession-box">
                      <div className="profession-box-top">
                        <div>
                          <h5>Business Details</h5>
                          <p>
                            Add your business name, category and contact
                            details.
                          </p>
                        </div>

                        <span className="box-icon">
                          <i className="feather-home"></i>
                        </span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Business Name</label>
                          <input
                            type="text"
                            name="business_name"
                            className="form-control settings-input"
                            placeholder="Business name"
                            value={formData.business_name}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            Business Category
                          </label>
                          <input
                            type="text"
                            name="business_category"
                            className="form-control settings-input"
                            placeholder="Textile / Hotel / Service"
                            value={formData.business_category}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Business Mobile</label>
                          <input
                            type="text"
                            name="business_mobile"
                            className="form-control settings-input"
                            placeholder="Mobile number"
                            value={formData.business_mobile}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Business Email</label>
                          <input
                            type="email"
                            name="business_email"
                            className="form-control settings-input"
                            placeholder="business@email.com"
                            value={formData.business_email}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="form-label">Business Address</label>
                          <input
                            type="text"
                            name="business_address"
                            className="form-control settings-input"
                            placeholder="Business address"
                            value={formData.business_address}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="form-label">
                            Website / Social Link
                          </label>
                          <input
                            type="text"
                            name="business_website"
                            className="form-control settings-input"
                            placeholder="https://example.com"
                            value={formData.business_website}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-12">
                          <label className="form-label">
                            Business Description
                          </label>
                          <textarea
                            name="business_description"
                            className="form-control settings-input"
                            rows={4}
                            placeholder="Briefly describe your business"
                            value={formData.business_description}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {workType === "" && (
                    <div className="profession-empty-box">
                      <div className="empty-profession-icon">
                        <i className="feather-user-x"></i>
                      </div>

                      <div>
                        <h5>No profession selected</h5>
                        <p>
                          Select <strong>Job</strong> or{" "}
                          <strong>Business</strong> if you want to show
                          professional details on your profile. Choose{" "}
                          <strong>None</strong> to keep this section clean.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="save-bar">
                    <div className="save-note">
                      Save your selected profession type. Choose None if you do
                      not want to show professional details.
                    </div>

                    <button
                      type="submit"
                      disabled={saveLoading}
                      className="save-btn"
                    >
                      <i className="feather-save"></i>
                      {saveLoading ? "Saving..." : "Save Details"}
                    </button>
                  </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Popupchat />
      <Appfooter />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

        .settings-page-main {
          width: 100%;
          min-height: 100vh;
          padding-top: 58px;
          padding-left: 285px;
          padding-right: 315px;
          padding-bottom: 40px;
          overflow-x: hidden;
          font-family: 'Inter', sans-serif;
          background: linear-gradient(180deg, #f8f9fc 0%, #f1f3f8 100%);
        }

        .settings-page-main .middle-sidebar-bottom,
        .settings-page-main .middle-sidebar-left {
          width: 100%;
          max-width: 100%;
        }

        .settings-shell {
          width: 100%;
          max-width: 900px;
          margin-left: 22px;
          margin-top: 28px;
          padding-bottom: 40px;
        }

        .settings-top-grid.guest-profile-only {
          grid-template-columns: minmax(0, 1fr);
        }

        .settings-top-grid.guest-profile-only .settings-profile-card {
          width: 100%;
        }

        .settings-top-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 270px;
          gap: 16px;
          margin-bottom: 16px;
          align-items: stretch;
        }

        .settings-profile-card,
        .settings-action-card,
        .settings-card {
          background: #ffffff;
          border: 1px solid #e9ecf2;
          border-radius: 16px;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
        }

        .settings-profile-card {
          position: relative;
          overflow: hidden;
          padding: 16px;
          min-width: 0;
        }

        .profile-hero-card {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 14px;
          background: linear-gradient(135deg, #ffffff 0%, #f7faff 58%, #f1f6ff 100%);
          border-color: #e2eaf6;
          box-shadow: 0 5px 16px rgba(10, 35, 84, 0.045);
        }

        .profile-hero-glow {
          display: none;
        }

        .profile-hero-main {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          gap: 18px;
          min-width: 0;
        }

        .settings-profile-avatar-wrap {
          position: relative;
          width: 78px;
          height: 78px;
          min-width: 78px;
        }

        .settings-profile-avatar {
          width: 78px;
          height: 78px;
          object-fit: cover;
          border-radius: 18px;
          border: 3px solid #ffffff;
          background: #ffffff;
          box-shadow: 0 6px 16px rgba(15, 23, 42, 0.10);
          display: block;
          user-select: none;
        }

        .settings-online-dot {
          position: absolute;
          right: 2px;
          bottom: 8px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #22c55e;
          border: 3px solid #ffffff;
          box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.12);
        }

        .settings-profile-info {
          min-width: 0;
          flex: 1;
        }

        .settings-chip,
        .section-tag {
          display: none;
        }

        .settings-profile-info h2 {
          margin: 0 0 4px;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 24px;
          font-weight: 700;
          letter-spacing: -0.01em;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .settings-profile-info p {
          margin: 0;
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .settings-mini-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 13px;
        }

        .settings-mini-tags span {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 11px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.78);
          border: 1px solid #DCE4F5;
          color: #475569;
          font-size: 12px;
          font-weight: 600;
          max-width: 190px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .settings-mini-tags .success-tag {
          color: #15803d;
          background: #f0fdf4;
          border-color: #bbf7d0;
        }

        .profile-hero-footer {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding-top: 14px;
          border-top: 1px solid #E3EAF8;
        }

        .profile-small-note {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          color: #64748b;
          font-size: 12.5px;
          font-weight: 500;
          line-height: 1.35;
        }

        .profile-small-note i {
          color: #2F6FE4;
          font-size: 15px;
          min-width: 15px;
        }

        .profile-small-note span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .edit-profile-main-btn {
          border: 1px solid #C7D9F8;
          background: #EAF1FF;
          color: #2F6FE4;
          border-radius: 999px;
          padding: 10px 16px;
          font-size: 13px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .edit-profile-main-btn:hover {
          background: #C7D9F8;
          border-color: #B9CCF5;
          transform: translateY(-1px);
          box-shadow: 0 8px 18px rgba(47, 111, 228, 0.14);
        }

        .settings-action-card {
          padding: 18px;
          min-height: 205px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .action-card-top {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .action-icon {
          width: 42px;
          height: 42px;
          min-width: 42px;
          border-radius: 15px;
          background: #fff7ed;
          color: #ea580c;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }

        .action-text {
          min-width: 0;
        }

        .action-tag {
          margin-bottom: 8px;
          background: #EAF1FF;
        }

        .settings-action-card h4,
        .settings-card-header h4,
        .selector-head h5,
        .profession-box-top h5 {
          margin: 0;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 17px;
          font-weight: 700;
          line-height: 1.3;
        }

        .settings-action-card h4 {
          font-size: 19px;
          letter-spacing: -0.2px;
        }

        .settings-action-card p,
        .settings-card-header p,
        .selector-head p,
        .profession-box-top p,
        .save-note {
          margin: 6px 0 0;
          color: #64748b;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.45;
        }

        .settings-action-card p {
          margin-top: 5px;
        }

        .session-box {
          margin-top: 14px;
          padding: 12px 13px;
          border-radius: 16px;
          background: #F4F7FC;
          border: 1px solid #E3EAF8;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }

        .session-box span {
          display: block;
          color: #94a3b8;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.25px;
          margin-bottom: 2px;
        }

        .session-box strong {
          display: block;
          color: #0A2354;
          font-size: 13px;
          font-weight: 700;
        }

        .session-box i {
          color: #22c55e;
          font-size: 18px;
        }

        .settings-logout-btn {
          margin-top: 14px;
          border: 1px solid #fed7aa;
          background: #fff7ed;
          color: #c2410c;
          border-radius: 15px;
          padding: 11px 16px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 700;
          width: 100%;
          transition: all 0.2s ease;
        }

        .settings-logout-btn:hover {
          background: #ffedd5;
          border-color: #fdba74;
          transform: translateY(-1px);
          box-shadow: 0 8px 18px rgba(234, 88, 12, 0.12);
        }

        .settings-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.6fr) 290px;
          gap: 16px;
          margin-bottom: 16px;
        }

        .settings-card {
          padding: 16px;
        }

        .settings-card-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 16px;
        }

        .settings-info-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .info-tile,
        .summary-box {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 10px;
          border-radius: 10px;
          background: #f8fafc;
          border: 1px solid #edf1f6;
          min-width: 0;
        }

        .info-icon,
        .summary-icon {
          width: 34px;
          height: 34px;
          min-width: 34px;
          border-radius: 9px;
          background: #eef4ff;
          color: #2F6FE4;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
        }

        .info-text,
        .summary-box > div:last-child {
          min-width: 0;
        }

        .info-text span,
        .summary-box span,
        .form-label {
          display: block;
          color: #94a3b8;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          margin-bottom: 4px;
          letter-spacing: 0.25px;
        }

        .info-text h5,
        .summary-box h6 {
          margin: 0;
          color: #0A2354;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .summary-stack {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .selector-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          flex-wrap: wrap;
          margin-bottom: 14px;
        }

        .single-choice-badge {
          padding: 8px 12px;
          border-radius: 999px;
          background: #fff7ed;
          color: #c2410c;
          border: 1px solid #fed7aa;
          font-size: 12px;
          font-weight: 500;
        }

        .profession-choice-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
          margin-bottom: 16px;
        }

        .work-type-card {
          width: 100%;
          border: 1px solid #e1e8f2;
          background: #ffffff;
          border-radius: 12px;
          padding: 12px;
          text-align: left;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          transition: all 0.2s ease;
          min-height: 76px;
        }

        .work-type-card:hover {
          border-color: #B9CCF5;
          background: #F4F7FC;
          transform: translateY(-1px);
        }

        .work-type-card.active {
          border-color: #2F6FE4;
          background: linear-gradient(135deg, #EAF1FF 0%, #ffffff 100%);
          box-shadow: 0 12px 28px rgba(47, 111, 228, 0.1);
        }

        .work-type-card.none-card.active {
          border-color: #64748b;
          background: linear-gradient(135deg, #F4F7FC 0%, #ffffff 100%);
          box-shadow: 0 12px 28px rgba(100, 116, 139, 0.1);
        }

        .work-left {
          display: flex;
          align-items: center;
          gap: 13px;
          min-width: 0;
        }

        .work-icon {
          width: 46px;
          height: 46px;
          min-width: 46px;
          border-radius: 15px;
          background: #EAF1FF;
          color: #2F6FE4;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 19px;
        }

        .none-card .work-icon {
          background: #f1f5f9;
          color: #64748b;
        }

        .work-copy strong {
          display: block;
          color: #0A2354;
          font-size: 15px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .work-copy small {
          display: block;
          color: #64748b;
          font-size: 12px;
          font-weight: 500;
          line-height: 1.35;
        }

        .check-dot {
          width: 24px;
          height: 24px;
          min-width: 24px;
          border-radius: 50%;
          background: #f1f5f9;
          color: transparent;
          border: 1px solid #DCE4F5;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
        }

        .work-type-card.active .check-dot {
          background: #2F6FE4;
          border-color: #2F6FE4;
          color: #ffffff;
        }

        .work-type-card.none-card.active .check-dot {
          background: #64748b;
          border-color: #64748b;
          color: #ffffff;
        }

        .profession-empty-box {
          border: 1px dashed #DCE4F5;
          border-radius: 20px;
          padding: 24px;
          background: #F4F7FC;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .empty-profession-icon {
          width: 56px;
          height: 56px;
          min-width: 56px;
          border-radius: 18px;
          background: #ffffff;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          border: 1px solid #DCE4F5;
        }

        .profession-empty-box h5 {
          margin: 0 0 5px;
          font-size: 16px;
          font-weight: 700;
          color: #0A2354;
        }

        .profession-empty-box p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.55;
          font-weight: 500;
        }

        .profession-box {
          border: 1px solid #edf1f6;
          border-radius: 12px;
          padding: 14px;
          background: #f8fafc;
        }

        .profession-box-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 15px;
          margin-bottom: 16px;
          padding-bottom: 14px;
          border-bottom: 1px solid #E3EAF8;
        }

        .box-icon {
          width: 42px;
          height: 42px;
          min-width: 42px;
          border-radius: 14px;
          background: #EAF1FF;
          color: #2F6FE4;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }

        .settings-input {
          min-height: 40px;
          border-radius: 10px;
          border: 1px solid #e5e8ef;
          font-size: 12px;
          font-weight: 400;
          color: #1f2937;
          box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
          background: #ffffff;
        }

        .settings-input:focus {
          border-color: #2F6FE4;
          box-shadow: 0 0 0 4px rgba(47, 111, 228, 0.08);
        }

        .save-bar {
          margin-top: 18px;
          padding-top: 16px;
          border-top: 1px solid #E3EAF8;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          flex-wrap: wrap;
        }

        .save-btn {
          min-height: 34px;
          border: 1px solid #2F6FE4;
          background: #2F6FE4;
          color: #ffffff;
          border-radius: 10px;
          padding: 0 12px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 700;
          box-shadow: none;
        }

        .save-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .settings-dialog-backdrop,
        .edit-profile-backdrop {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(10, 35, 84, 0.48);
          backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
        }

        .settings-dialog-box,
        .edit-profile-modal {
          width: 100%;
          max-width: 480px;
          background: #ffffff;
          border-radius: 22px;
          padding: 22px;
          border: 1px solid #DCE4F5;
          box-shadow: 0 24px 70px rgba(10, 35, 84, 0.24);
        }

        .settings-dialog-box {
          max-width: 430px;
          text-align: center;
        }

        .settings-dialog-icon {
          width: 62px;
          height: 62px;
          margin: 0 auto 16px;
          border-radius: 20px;
          background: #fff7ed;
          color: #f97316;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
        }

        .settings-dialog-box h4 {
          margin: 0 0 8px;
          color: #0A2354;
          font-size: 20px;
          font-weight: 600;
        }

        .settings-dialog-box p {
          margin: 0;
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
          line-height: 1.6;
        }

        .settings-dialog-actions,
        .edit-profile-actions {
          display: flex;
          gap: 12px;
          margin-top: 22px;
        }

        .settings-dialog-actions button,
        .edit-cancel-btn,
        .edit-save-btn {
          flex: 1;
          border: none;
          border-radius: 14px;
          padding: 12px 14px;
          font-size: 14px;
          font-weight: 500;
        }

        .dialog-cancel-btn,
        .edit-cancel-btn {
          background: #f1f5f9;
          color: #475569;
        }

        .dialog-confirm-btn,
        .edit-save-btn {
          background: #2F6FE4;
          color: #ffffff;
          box-shadow: 0 10px 20px rgba(47, 111, 228, 0.16);
        }

        .edit-profile-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 18px;
        }

        .edit-profile-head span {
          display: inline-flex;
          padding: 6px 12px;
          border-radius: 999px;
          background: #EAF1FF;
          color: #2F6FE4;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .edit-profile-head h4 {
          margin: 0;
          color: #0A2354;
          font-size: 20px;
          font-weight: 650;
        }

        .edit-profile-head button {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 0;
          background: #f1f5f9;
          color: #475569;
          font-size: 22px;
          line-height: 1;
        }

        .edit-profile-photo-row {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px;
          border-radius: 18px;
          background: #F4F7FC;
          border: 1px solid #E3EAF8;
          margin-bottom: 16px;
        }

        .edit-profile-photo-row img {
          width: 82px;
          height: 82px;
          min-width: 82px;
          border-radius: 22px;
          object-fit: cover;
          background: #D7E1F5;
          border: 3px solid #ffffff;
          box-shadow: 0 6px 16px rgba(10, 35, 84, 0.08);
        }

        .photo-upload-btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 9px 13px;
          border-radius: 999px;
          background: #EAF1FF;
          border: 1px solid #C7D9F8;
          color: #2F6FE4;
          font-size: 13px;
          font-weight: 650;
          cursor: pointer;
          margin-bottom: 6px;
        }

        .edit-profile-photo-row p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          font-weight: 500;
        }

        .edit-save-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 1400px) {
          .settings-page-main {
            padding-right: 290px;
          }
        }

        @media (max-width: 1200px) {
          .settings-page-main {
            padding-left: 20px;
            padding-right: 20px;
          }

          .settings-shell {
            margin-left: 0;
            max-width: 100%;
          }

          .settings-top-grid,
          .settings-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 1366px) {

        }

        @media (max-width: 1100px) {
          .settings-top-grid,
          .settings-grid {
            grid-template-columns: 1fr;
          }

          .settings-action-card,
          .settings-profile-card {
            min-height: auto;
          }
        }

        @media (max-width: 991px) {

          .settings-info-grid,
          .profession-choice-grid {
            grid-template-columns: 1fr;
          }

          .profession-empty-box {
            align-items: flex-start;
          }
        }

        @media (max-width: 768px) {
          .settings-page-main {
            padding-top: 16px;
            padding-left: 12px;
            padding-right: 12px;
          }

          .settings-page-main .middle-sidebar-bottom,
          .settings-page-main .middle-sidebar-left {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .settings-shell {
            margin-top: 0;
            margin-left: 0;
            max-width: 100%;
            padding-bottom: 76px;
          }

          .settings-profile-card,
          .settings-action-card,
          .settings-card {
            border-radius: 14px;
          }

          .settings-info-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 575px) {

          .profile-hero-main {
            align-items: flex-start;
            gap: 14px;
          }

          .settings-profile-avatar,
          .settings-profile-avatar-wrap {
            width: 82px;
            height: 82px;
            min-width: 82px;
          }

          .settings-profile-info h2 {
            font-size: 22px;
            white-space: normal;
          }

          .settings-profile-info p {
            white-space: normal;
          }

          .profile-hero-footer {
            flex-direction: column;
            align-items: stretch;
          }

          .profile-small-note span {
            white-space: normal;
          }

          .action-card-top {
            align-items: center;
          }

          .save-btn,
          .edit-cancel-btn,
          .edit-save-btn {
            width: 100%;
            justify-content: center;
          }

          .edit-profile-photo-row {
            align-items: flex-start;
          }

          .edit-profile-actions,
          .settings-dialog-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default Settings;
